import React, { useState } from "react";
import { Box, Button, Chip, Popover, TextField, Typography } from "@mui/material";
import { useWidgetMsg } from "../../hooks/widget/useWidgetMsg";
import { OTHER_CHIP, FREE_TEXT_MAX, chipsFor, clampFreeText, toggleChip, chipPayload } from "../../utils/feedback";

const PRIMARY = "#6257FF";

/**
 * The optional chip popover that opens after a thumb is recorded (FR-10 / FR-10a).
 *
 * Seven stable keys per direction, `other` last, multi-select, labelled through
 * `feedback.chip_<key>` (never English keyed by English). `other` — and nothing else — reveals a
 * 500-character field with a live counter; de-selecting `other` discards the text. Send is
 * disabled with no chip (the thumb alone already counted). Skip, Escape and a backdrop click all
 * close through `onSkip` and none of them touches the rating. Free text is sent raw and never
 * rendered back as HTML.
 */
export default function FeedbackChips({ open, anchorEl, rating, onSend, onSkip, busy = false, errorText = null }) {
	const { msg, isRtl } = useWidgetMsg();
	const [selected, setSelected] = useState([]);
	const [text, setText] = useState("");
	const keys = chipsFor(rating);
	const showField = selected.includes(OTHER_CHIP);

	const toggle = (key) => {
		const { selected: next, discardText } = toggleChip(selected, key);
		setSelected(next);
		if (discardText) setText("");
	};

	const titleId = `feedback-chips-title-${rating}`;

	return (
		<Popover
			open={!!open && !!anchorEl}
			anchorEl={anchorEl}
			onClose={onSkip}
			anchorOrigin={{ vertical: "bottom", horizontal: isRtl ? "right" : "left" }}
			transformOrigin={{ vertical: "top", horizontal: isRtl ? "right" : "left" }}
			PaperProps={{ sx: { borderRadius: "14px", boxShadow: "0 4px 18px rgba(0,0,0,0.08)", border: "1px solid rgba(0,0,0,0.10)", maxWidth: 320 } }}
		>
			<Box
				role="dialog"
				aria-labelledby={titleId}
				dir={isRtl ? "rtl" : "ltr"}
				className="feedback-chips"
				sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1.1 }}
			>
				<Typography id={titleId} sx={{ fontSize: 13, fontWeight: 600 }}>
					{rating === "up"
						? msg("feedback.title_up", "What was good about it?")
						: msg("feedback.title_down", "What was wrong with it?")}
				</Typography>
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
					{keys.map((key) => {
						const on = selected.includes(key);
						return (
							<Chip
								key={key}
								data-chip={key}
								label={msg(`feedback.chip_${key}`, key)}
								aria-pressed={on}
								clickable
								onClick={() => toggle(key)}
								variant={on ? "filled" : "outlined"}
								size="small"
								sx={{
									fontSize: 12.5,
									fontWeight: 500,
									borderRadius: "999px",
									...(on
										? { bgcolor: "rgba(98,87,255,0.12)", color: PRIMARY, border: "1px solid rgba(98,87,255,0.4)", "&:hover": { bgcolor: "rgba(98,87,255,0.18)" } }
										: { color: "rgba(0,0,0,0.7)", borderColor: "rgba(0,0,0,0.12)" }),
								}}
							/>
						);
					})}
				</Box>
				{showField && (
					<TextField
						multiline
						minRows={2}
						maxRows={4}
						size="small"
						value={text}
						autoFocus
						onChange={(e) => setText(clampFreeText(e.target.value))}
						placeholder={msg("feedback.other_placeholder", "Tell us more (optional)")}
						inputProps={{ maxLength: FREE_TEXT_MAX, "aria-label": msg("feedback.other_placeholder", "Tell us more (optional)") }}
						FormHelperTextProps={{ "aria-live": "polite", sx: { textAlign: isRtl ? "left" : "right", mx: 0 } }}
						helperText={msg("feedback.counter", "{count}/{max}", { count: text.length, max: FREE_TEXT_MAX })}
						sx={{ "& .MuiOutlinedInput-root": { fontSize: 12.5, borderRadius: "9px", background: "#FAFAFC" } }}
					/>
				)}
				{errorText && (
					<Typography role="alert" sx={{ fontSize: 12, color: "#d32f2f" }}>{errorText}</Typography>
				)}
				<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
					<Button size="small" onClick={onSkip} sx={{ textTransform: "none", color: "rgba(0,0,0,0.7)" }}>
						{msg("feedback.skip", "Skip")}
					</Button>
					<Button
						size="small"
						variant="contained"
						disabled={selected.length === 0 || busy}
						onClick={() => onSend(chipPayload(selected, text))}
						sx={{ textTransform: "none", boxShadow: "none", bgcolor: PRIMARY, "&:hover": { bgcolor: "#4F46E5", boxShadow: "none" } }}
					>
						{msg("feedback.send", "Send")}
					</Button>
				</Box>
			</Box>
		</Popover>
	);
}
